import { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';

function PlacesPage() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [imageLoading, setImageLoading] = useState({});
  const navigate = useNavigate();

  // Fallback static places data (shown if no data in Firestore)
  const fallbackPlaces = [
    {
      id: 1,
      title: "Beaconsfield Mine and Heritage Centre",
      category: "Heritage",
      image: "/images/beaconsfield.jpg",
      description: "Museum showcasing Tasmania's gold mining history with underground mine experiences and the story of the 2006 mine rescue.",
      website: "https://www.beaconsfield-mine.com.au"
    },
    {
      id: 2,
      title: "Miners Gold Brewery",
      category: "Food & Drink",
      image: "/images/Brewery.jpg",
      description: "Local craft brewery in Beaconsfield offering beer tastings and celebrating the area's mining heritage."
    },
    {
      id: 3,
      title: "Holy Trinity Anglican Church",
      category: "Heritage",
      image: "/images/church.jpg",
      description: "Historic Anglican church in Beaconsfield, a heritage building that has served the local community for generations."
    },
    {
      id: 4,
      title: "Beaconsfield Skate Park",
      category: "Recreation",
      image: "/images/skate.jpg",
      description: "Community skate park facility in Beaconsfield providing space for skateboarding and BMX activities."
    },
    {
      id: 5,
      title: "Bruen Waterwheel",
      category: "Heritage",
      image: "/images/sculpture.jpg",
      description: "Historic waterwheel structure in Beaconsfield, a heritage landmark from the town's gold mining era."
    },
    {
      id: 6,
      title: "Moon Lily Kitchen And Cakes",
      category: "Food & Drink",
      image: "/images/lilly.webp",
      description: "Local café and bakery in Beaconsfield offering cakes, pastries, and coffee."
    },
    {
      id: 7,
      title: "Tassie Dhaba Cafe And Indian Cuisine",
      category: "Food & Drink",
      image: "/images/tassie-dhaba-cafe-and.jpg",
      description: "Indian cuisine café in Beaconsfield serving traditional Indian dishes and café fare."
    },
    {
      id: 8,
      title: "Jubilee Bakery Beaconsfield",
      category: "Food & Drink",
      image: "/images/jubilee.jpg",
      description: "Local bakery in Beaconsfield offering fresh bread, pastries, and baked goods for the community."
    }
  ];

  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Fetch places from Firestore
  useEffect(() => {
    fetchPlaces();
  }, []);

  const fetchPlaces = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, 'places'));
      const placesData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Use Firestore data if available, otherwise use fallback
      setPlaces(placesData.length > 0 ? placesData : fallbackPlaces);
    } catch (error) {
      console.error('Error fetching places:', error);
      // Use fallback data if Firestore fetch fails
      setPlaces(fallbackPlaces);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (placeId) => {
    if (!window.confirm('Are you sure you want to delete this place?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'places', placeId));
      alert('Place deleted successfully!');
      fetchPlaces();
    } catch (error) {
      console.error('Error deleting place:', error);
      alert('Failed to delete place');
    }
  };

  const handleImageLoad = (placeId) => {
    setImageLoading(prev => ({
      ...prev,
      [placeId]: false
    }));
  };

  const handleImageError = (placeId) => {
    setImageLoading(prev => ({
      ...prev,
      [placeId]: false
    }));
  };

  return (
    <section id="places" className="py-20">
      <div className="container-width section-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Local <span className="text-secondary">Places</span>
          </h2>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            Explore the heart of our community through its remarkable places. From historic landmarks to vibrant gathering spots.
          </p>
          
          {/* Admin Controls */}
          {user && (
            <div className="mt-6">
              <button
                onClick={() => navigate('/manage-places')}
                className="btn-primary inline-flex items-center gap-2"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                </svg>
                Manage Places
              </button>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center text-white py-20">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
            <p className="mt-4">Loading places...</p>
          </div>
        ) : (
          <>
            {/* Places Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {places.map((place) => (
                <div 
                  key={place.id}
                  className="overflow-hidden group relative"
                >
                  {/* Clickable Link Wrapper */}
                  {place.website ? (
                    <a
                      href={place.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block h-full cursor-pointer"
                    >
                      {/* Place Image */}
                      <div className="relative h-48 overflow-hidden bg-gray-800">
                        {imageLoading[place.id] !== false && (
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse"></div>
                        )}
                        <img 
                          src={place.image} 
                          alt={place.title}
                          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${imageLoading[place.id] === false ? 'opacity-100' : 'opacity-0'}`}
                          loading="lazy"
                          decoding="async"
                          onLoad={() => handleImageLoad(place.id)}
                          onError={() => handleImageError(place.id)}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-4 left-4">
                          <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                            {place.category}
                          </span>
                        </div>
                      </div>

                      {/* Place Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-primary-300 transition-colors duration-200">
                          {place.title}
                        </h3>
                        <p className="text-gray-200 text-sm leading-relaxed mb-4">
                          {place.description}
                        </p>
                        <div className="inline-flex items-center gap-2 text-primary-400 group-hover:text-primary-300 font-medium transition-colors">
                          Visit Website
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M11 3a1 1 0 100 2h3.586L9.293 9.293a1 1 0 001.414 1.414L16 6.414V10a1 1 0 102 0V4a1 1 0 00-1-1h-6z" />
                            <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z" />
                          </svg>
                        </div>
                      </div>
                    </a>
                  ) : (
                    <>
                      {/* Place Image */}
                      <div className="relative h-48 overflow-hidden bg-gray-800">
                        {imageLoading[place.id] !== false && (
                          <div className="absolute inset-0 bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800 animate-pulse"></div>
                        )}
                        <img 
                          src={place.image} 
                          alt={place.title}
                          className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-300 ${imageLoading[place.id] === false ? 'opacity-100' : 'opacity-0'}`}
                          loading="lazy"
                          decoding="async"
                          onLoad={() => handleImageLoad(place.id)}
                          onError={() => handleImageError(place.id)}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                        <div className="absolute bottom-4 left-4">
                          <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                            {place.category}
                          </span>
                        </div>
                      </div>

                      {/* Place Content */}
                      <div className="p-6">
                        <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-primary-300 transition-colors duration-200">
                          {place.title}
                        </h3>
                        <p className="text-gray-200 text-sm leading-relaxed">
                          {place.description}
                        </p>
                      </div>
                    </>
                  )}

                  {/* Quick Delete for Admins */}
                  {user && typeof place.id === 'string' && (
                    <div className="absolute top-2 right-2">
                      <button
                        onClick={() => handleDelete(place.id)}
                        className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full shadow-lg transition-colors"
                        title="Delete place"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* CTA Section */}
            <div className="text-center mt-16">
              <div className="p-8 md:p-12">
                <h3 className="text-3xl font-bold text-white mb-6">
                  Ready to Join Our Community?
                </h3>
                <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
                  For any membership inquiries, partnership opportunities, or general information, please feel free to 
                  contact us or visit us at the Exeter markets. We are always delighted to welcome new members and 
                  collaborate with individuals who share our vision for a thriving business community in the Tamar Valley.
                </p>
                <p className="text-lg text-gray-300 mb-8 max-w-3xl mx-auto">
                  Remember, together we can nurture economic growth, create sustainable businesses, and strengthen the 
                  Tamar Valley's position as a hub for innovation and prosperity. Join TVBA today and take advantage of 
                  the endless possibilities that await you in our breathtaking region!
                </p>
                <div className="flex justify-center">
                  <a 
                    href="/contact"
                    className="btn-primary"
                  >
                    Contact Us
                  </a>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  )
}

export default PlacesPage
